"use client";

import React, { useRef } from "react";
import {
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  HStack,
  Text,
  useToast,
  IconButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  VStack,
  ButtonGroup,
  Box,
  Flex,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from "@chakra-ui/react";
import { FaPlayCircle, FaStopCircle } from "react-icons/fa";
import { ChevronDownIcon, SettingsIcon } from "@chakra-ui/icons";
import {
  agentService,
  type VoiceDetails,
} from "../../services/agentService.ts";
import { useAppTheme } from "../../ThemeContext.tsx";

import GenderImage from "./voices/GenderImages.tsx";

const PLAYHT_VOICE_MODELS = [
  {
    id: "Play3.0-mini",
    label: "Play 3.0 mini",
    description: "Fast, high quality",
  },
  {
    id: "PlayDialog",
    label: "Play Dialog",
    description: "Slow, highest quality",
  },
];

const ELEVENLABS_VOICE_MODELS = [
  {
    id: "eleven_turbo_v2",
    label: "Elevenlabs Turbo V2",
    description: "English only, fast, high quality",
  },
  {
    id: "eleven_flash_v2",
    label: "Elevenlabs Flash V2",
    description: "English only, fastest, medium quality",
  },
  {
    id: "eleven_turbo_v2_5",
    label: "Elevenlabs Turbo V2.5",
    description: "Multilingual, fast, high quality",
  },
  {
    id: "eleven_flash_v2_5",
    label: "Elevenlabs Flash V2.5",
    description: "Multilingual, fastest, medium quality",
  },
  {
    id: "eleven_multilingual_v2",
    label: "Elevenlabs Multilingual v2",
    description: "Multilingual, slow, highest quality",
  },
];

interface VoiceSelectProps {
  initialVoice: VoiceDetails | null;
  voices: VoiceDetails[];
  agentId: string;
  setSelectedVoice: React.Dispatch<React.SetStateAction<VoiceDetails | null>>;
}

interface VoiceSettings {
  speed: number;
  temperature: number;
  volume: number;
  voice_model: string | null;
}

export const VoiceSelect: React.FC<VoiceSelectProps> = React.memo(
  ({ initialVoice, voices, agentId, setSelectedVoice }) => {
    const [selectedVoice, setSelectedVoiceState] =
      React.useState<VoiceDetails | null>(initialVoice);

    // Update internal state when initialVoice changes
    React.useEffect(() => {
      if (initialVoice) {
        setSelectedVoiceState(initialVoice);
      }
    }, [initialVoice]);

    const [isSettingsOpen, setIsSettingsOpen] = React.useState(false);
    const [currentlyPlaying, setCurrentlyPlaying] = React.useState<
      string | null
    >(null);
    const [isMenuOpen, setIsMenuOpen] = React.useState(false);
    const [voiceTabIndex, setVoiceTabIndex] = React.useState(0);
    const audioRef = React.useRef<HTMLAudioElement | null>(null);
    const toast = useToast();
    const { resolvedTheme } = useAppTheme();
    const isDark = resolvedTheme === "dark";
    const previousProviderRef = useRef<string | null>(
      initialVoice?.provider || null
    );

    const [voiceSettings, setVoiceSettings] = React.useState<VoiceSettings>({
      speed: 1.0,
      temperature: 1.0,
      volume: 1.0,
      voice_model: null,
    });
    const [tempSettings, setTempSettings] =
      React.useState<VoiceSettings>(voiceSettings);

    // Get unique providers from voices array
    const providers = React.useMemo(() => {
      const uniqueProviders = Array.from(
        new Set(
          voices
            .map((voice) => voice.provider)
            .filter((provider) => provider !== "playht")
        )
      );
      return uniqueProviders.sort();
    }, [voices]);

    React.useEffect(() => {
      if (voiceTabIndex >= providers.length && providers.length > 0) {
        setVoiceTabIndex(0);
      }
    }, [providers.length, voiceTabIndex]);

    const openVoiceMenu = React.useCallback(() => {
      setIsMenuOpen(true);
      const p = selectedVoice?.provider;
      const idx = p ? providers.indexOf(p) : -1;
      setVoiceTabIndex(idx >= 0 ? idx : 0);
    }, [selectedVoice?.provider, providers]);

    const isPlayHTVoice = React.useMemo(() => {
      return selectedVoice?.provider === "play";
    }, [selectedVoice]);

    const isElevenLabsVoice = React.useMemo(() => {
      return selectedVoice?.provider === "elevenlabs";
    }, [selectedVoice]);

    // Helper function to determine if the voice model is compatible with the provider
    const isVoiceModelCompatible = (
      provider: string | undefined,
      voiceModel: string | null
    ): boolean => {
      if (!provider || !voiceModel) return false;

      if (provider === "play" || provider === "playht") {
        return voiceModel.startsWith("Play");
      } else if (provider === "elevenlabs") {
        return voiceModel.startsWith("eleven_");
      }

      return false;
    };

    React.useEffect(() => {
      // Create audio element
      audioRef.current = new Audio();

      // Cleanup on unmount
      return () => {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current = null;
        }
      };
    }, []);

    // Stop audio when menu closes
    React.useEffect(() => {
      if (!isMenuOpen && audioRef.current && currentlyPlaying) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        setCurrentlyPlaying(null);
      }
    }, [isMenuOpen, currentlyPlaying]);

    const playAudio = React.useCallback(
      (voiceId: string, audioUrl: string | undefined, e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent menu item click

        if (!audioUrl) {
          toast({
            title: "Preview not available",
            description: "No preview audio available for this voice",
            status: "info",
            duration: 2000,
          });
          return;
        }

        // Stop current audio if playing
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }

        // If clicking on the same voice that's already playing, just stop it
        if (currentlyPlaying === voiceId) {
          setCurrentlyPlaying(null);
          return;
        }

        // Play new audio
        if (audioRef.current) {
          audioRef.current.src = audioUrl;
          audioRef.current.play().catch((error) => {
            console.error("Error playing audio:", error);
            toast({
              title: "Error playing preview",
              description: "Could not play the audio preview",
              status: "error",
              duration: 2000,
            });
          });

          setCurrentlyPlaying(voiceId);

          // Reset when audio ends
          audioRef.current.onended = () => {
            setCurrentlyPlaying(null);
          };
        }
      },
      [currentlyPlaying, toast]
    );

    const fetchAgentDetails = React.useCallback(async () => {
      try {
        const data = await agentService.getAgentDetails(agentId);
        let voiceModel = data.voice_model;

        // Only set default voice model if it's compatible with the current provider
        if (!voiceModel) {
          if (isPlayHTVoice) {
            voiceModel = "Play3.0-mini";
          } else if (isElevenLabsVoice) {
            voiceModel = "eleven_turbo_v2";
          }
        } else {
          // Check if the current voice model is compatible with the provider
          if (
            (isPlayHTVoice && !voiceModel.startsWith("Play")) ||
            (isElevenLabsVoice && !voiceModel.startsWith("eleven_"))
          ) {
            // If not compatible, set to null
            voiceModel = null;
          }
        }

        setVoiceSettings({
          speed: data.voice_speed !== undefined ? data.voice_speed : 1.0,
          temperature:
            data.voice_temperature !== undefined ? data.voice_temperature : 1.0,
          volume: data.volume !== undefined ? data.volume : 1.0,
          voice_model: voiceModel,
        });
        setTempSettings({
          speed: data.voice_speed !== undefined ? data.voice_speed : 1.0,
          temperature:
            data.voice_temperature !== undefined ? data.voice_temperature : 1.0,
          volume: data.volume !== undefined ? data.volume : 1.0,
          voice_model: voiceModel,
        });
      } catch (error) {
        console.error("Error fetching agent details:", error);
        toast({
          title: "Error fetching agent details",
          description: "Unable to load current voice settings",
          status: "error",
          duration: 3000,
        });
      }
    }, [agentId, isPlayHTVoice, isElevenLabsVoice, toast]);

    const handleSettingsClick = () => {
      fetchAgentDetails();
      setIsSettingsOpen(true);
    };

    const handleVoiceChange = React.useCallback(
      async (voice: VoiceDetails) => {
        try {
          // Check if provider has changed
          const currentProvider = selectedVoice?.provider;
          const newProvider = voice.provider;
          const hasProviderChanged = currentProvider !== newProvider;

          console.log(
            `Voice change: ${currentProvider} -> ${newProvider}, changed: ${hasProviderChanged}`
          );

          // If provider has changed, don't send voice_model
          if (hasProviderChanged) {
            await agentService.update(agentId, {
              voice_id: voice.voice_id,
              voice_model: null,
            });
            previousProviderRef.current = newProvider;
          } else {
            await agentService.update(agentId, {
              voice_id: voice.voice_id,
            });
          }

          setSelectedVoiceState(voice);
          setSelectedVoice(voice);
          setIsMenuOpen(false);
          toast({
            title: "Voice updated successfully",
            status: "success",
            duration: 2000,
          });
        } catch (error: any) {
          console.error("Error updating voice:", error);
          toast({
            title: "Error updating voice",
            description:
              error.response?.data?.message || "An unexpected error occurred",
            status: "error",
            duration: 3000,
          });
        }
      },
      [agentId, selectedVoice, setSelectedVoice, toast]
    );

    const handleSettingsSave = async () => {
      try {
        // Check if the current voice model is compatible with the provider
        const isCompatible = isVoiceModelCompatible(
          selectedVoice?.provider,
          tempSettings.voice_model
        );

        // Check if provider has changed since last save
        const currentProvider = selectedVoice?.provider;
        const previousProvider = previousProviderRef.current;
        const hasProviderChanged = currentProvider !== previousProvider;

        console.log(
          `Settings save: ${previousProvider} -> ${currentProvider}, changed: ${hasProviderChanged}, model compatible: ${isCompatible}`
        );

        // Create request body based on compatibility and provider change
        const requestBody: any = {
          voice_speed: tempSettings.speed,
          voice_temperature: tempSettings.temperature,
          volume: tempSettings.volume,
        };

        // Only include voice_model if it's compatible and provider hasn't changed
        if (isCompatible && !hasProviderChanged) {
          requestBody.voice_model = tempSettings.voice_model;
        } else {
          console.log(
            "Excluding voice_model from request due to incompatibility or provider change"
          );
        }

        console.log("Request body:", requestBody);

        await agentService.updateAgentSettings(agentId, requestBody);

        // Update previous provider reference
        previousProviderRef.current = currentProvider;

        setVoiceSettings(tempSettings);
        setIsSettingsOpen(false);
        toast({
          title: "Voice settings updated successfully",
          status: "success",
          duration: 2000,
        });
      } catch (error: any) {
        console.error("Error updating voice settings:", error);
        toast({
          title: "Error updating voice settings",
          description:
            error.response?.data?.message || "An unexpected error occurred",
          status: "error",
          duration: 3000,
        });
      }
    };

    const handleSettingsCancel = () => {
      setTempSettings(voiceSettings);
      setIsSettingsOpen(false);
    };

    // Format provider name for display
    const formatProviderName = (provider: string) => {
      return provider.charAt(0).toUpperCase() + provider.slice(1);
    };

    // Render voice item
    const renderVoiceItem = (voice: VoiceDetails) => {
      const isRowSelected = voice.voice_id === selectedVoice?.voice_id;
      return (
      <MenuItem
        key={voice.voice_id}
        onClick={() => handleVoiceChange(voice)}
        bg={isRowSelected ? (isDark ? "gray.700" : "gray.100") : "transparent"}
        fontWeight={isRowSelected ? "semibold" : "normal"}
        _hover={{
          bg: isDark ? "gray.600" : "gray.50",
        }}
        color={isDark ? "white" : "gray.800"}
        py={3}
      >
        <Flex direction="column" width="100%">
          <Flex align="center" width="100%">
            <IconButton
              aria-label="Play voice sample"
              icon={
                <Box
                  as="span"
                  fontSize="sm"
                  color={isDark ? "gray.400" : "gray.600"}
                  borderRadius="full"
                >
                  {currentlyPlaying === voice.voice_id ? (
                    <FaStopCircle />
                  ) : (
                    <FaPlayCircle />
                  )}
                </Box>
              }
              size="xs"
              variant="ghost"
              mr={2}
              onClick={(e) =>
                playAudio(voice.voice_id, voice.preview_audio_url, e)
              }
            />
            <GenderImage voice={voice} isDark={isDark} />
            <Text fontWeight="medium">{voice?.voice_name}</Text>
          </Flex>
          <Flex
            ml="36px"
            mt={1}
            fontSize="xs"
            color={isDark ? "gray.400" : "gray.600"}
          >
            <Text mr={2}>{voice.gender}</Text>
            {voice.accent && (
              <>
                <Text mr={2}>•</Text>
                <Text mr={2}>{voice.accent}</Text>
              </>
            )}
            {voice.age && (
              <>
                <Text mr={2}>•</Text>
                <Text>{voice.age}</Text>
              </>
            )}
          </Flex>
        </Flex>
      </MenuItem>
      );
    };

    return (
      <HStack spacing={1}>
        <Menu
          isOpen={isMenuOpen}
          onOpen={openVoiceMenu}
          onClose={() => setIsMenuOpen(false)}
        >
          <MenuButton
            as={Button}
            variant="outline"
            size="sm"
            borderColor={isDark ? "gray.600" : "gray.300"}
            color={isDark ? "white" : "gray.800"}
            rightIcon={<ChevronDownIcon />}
          >
            <HStack spacing={2} color={isDark ? "white" : "gray.800"}>
              <GenderImage voice={selectedVoice} isDark={isDark} />
              <Text>{selectedVoice?.voice_name || "Select Voice"}</Text>
            </HStack>
          </MenuButton>

          <MenuList
            bg={isDark ? "gray.800" : "white"}
            borderColor={isDark ? "gray.600" : "gray.300"}
            p={0}
            sx={{
              // Chrome, Safari, and Edge
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: isDark ? "gray.700" : "gray.100",
              },
              "&::-webkit-scrollbar-thumb": {
                background: isDark ? "gray.600" : "gray.300",
                borderRadius: "6px",
              },
              // Firefox
              scrollbarWidth: "thin",
              scrollbarColor: isDark
                ? "#4A5568 #2D3748" /* thumb #4A5568, track #2D3748 */
                : "#CBD5E0 #EDF2F7" /* thumb #CBD5E0, track #EDF2F7 */,
            }}
          >
            <Tabs
              variant="enclosed"
              isFitted
              size="sm"
              index={providers.length > 0 ? voiceTabIndex : 0}
              onChange={setVoiceTabIndex}
            >
              <TabList>
                {providers.map((provider) => (
                  <Tab
                    key={provider}
                    _selected={{
                      color: isDark ? "white" : "gray.800",
                      bg: isDark ? "gray.700" : "gray.100",
                      fontWeight: "semibold",
                      borderColor: isDark ? "gray.500" : "gray.300",
                    }}
                    color={isDark ? "gray.300" : "gray.600"}
                    bg={isDark ? "gray.800" : "white"}
                    borderBottomWidth="1px"
                    borderBottomColor={isDark ? "gray.600" : "gray.200"}
                    _hover={{ bg: isDark ? "gray.700" : "gray.50" }}
                  >
                    {formatProviderName(provider)}
                  </Tab>
                ))}
              </TabList>
              <TabPanels>
                {providers.map((provider) => (
                  <TabPanel key={provider} p={0} maxH="300px" overflowY="auto">
                    {voices
                      .filter((voice) => voice.provider === provider)
                      .map(renderVoiceItem)}
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>
          </MenuList>
        </Menu>

        <Popover
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          placement="bottom-start"
          closeOnBlur={false}
        >
          <PopoverTrigger>
            <IconButton
              color={isDark ? "white" : "gray.800"}
              aria-label="Voice settings"
              icon={<SettingsIcon />}
              size="sm"
              variant="ghost"
              onClick={handleSettingsClick}
            />
          </PopoverTrigger>
          <PopoverContent
            bg={isDark ? "gray.800" : "white"}
            borderColor={isDark ? "gray.600" : "gray.300"}
            w="350px"
          >
            <PopoverBody p={4}>
              <VStack spacing={4} align="stretch">
                {(isPlayHTVoice || isElevenLabsVoice) && (
                  <Box>
                    <Text fontWeight="medium" mb={2}>
                      Voice Model
                    </Text>
                    <VStack align="stretch" spacing={2} pl={1}>
                      {(isPlayHTVoice
                        ? PLAYHT_VOICE_MODELS
                        : ELEVENLABS_VOICE_MODELS
                      ).map((model) => (
                        <Box
                          key={model.id}
                          onClick={() =>
                            setTempSettings((prev) => ({
                              ...prev,
                              voice_model: model.id,
                            }))
                          }
                          cursor="pointer"
                        >
                          <Flex align="center">
                            <Box
                              as="span"
                              mr={2}
                              borderWidth="1px"
                              borderRadius="full"
                              width="14px"
                              height="14px"
                              display="inline-flex"
                              alignItems="center"
                              justifyContent="center"
                              borderColor={isDark ? "gray.400" : "gray.500"}
                            >
                              {(tempSettings.voice_model === model.id ||
                                (isPlayHTVoice &&
                                  model.id === "Play3.0-mini" &&
                                  !tempSettings.voice_model) ||
                                (isElevenLabsVoice &&
                                  model.id === "eleven_turbo_v2" &&
                                  !tempSettings.voice_model)) && (
                                <Box
                                  as="span"
                                  borderRadius="full"
                                  width="8px"
                                  height="8px"
                                  bg="gray.500"
                                />
                              )}
                            </Box>
                            <Text fontWeight="medium">{model.label}</Text>
                          </Flex>
                          <Text fontSize="xs" color="gray.500" ml="22px">
                            {model.description}
                          </Text>
                        </Box>
                      ))}
                    </VStack>
                    <Box
                      borderBottomWidth="1px"
                      borderColor={isDark ? "gray.600" : "gray.200"}
                      my={2}
                    />
                  </Box>
                )}

                <Box>
                  <Flex justify="space-between" align="center" mb={1}>
                    <Text fontWeight="medium">Voice Speed</Text>
                    <Text fontSize="sm" color="gray.400">
                      {tempSettings.speed.toFixed(2)}
                    </Text>
                  </Flex>
                  <Slider
                    value={tempSettings.speed}
                    onChange={(value) =>
                      setTempSettings((prev) => ({ ...prev, speed: value }))
                    }
                    min={0.5}
                    max={2.0}
                    step={0.01}
                    focusThumbOnChange={false}
                  >
                    <SliderTrack bg={isDark ? "gray.600" : "gray.200"}>
                      <SliderFilledTrack bg="gray.500" />
                    </SliderTrack>
                    <SliderThumb boxSize={4} />
                  </Slider>
                </Box>

                <Box>
                  <Flex justify="space-between" align="center" mb={1}>
                    <Text fontWeight="medium">Voice Temperature</Text>
                    <Text fontSize="sm" color="gray.400">
                      {tempSettings.temperature.toFixed(2)}
                    </Text>
                  </Flex>
                  <Slider
                    value={tempSettings.temperature}
                    onChange={(value) =>
                      setTempSettings((prev) => ({
                        ...prev,
                        temperature: value,
                      }))
                    }
                    min={0}
                    max={2.0}
                    step={0.01}
                    focusThumbOnChange={false}
                  >
                    <SliderTrack bg={isDark ? "gray.600" : "gray.200"}>
                      <SliderFilledTrack bg="gray.500" />
                    </SliderTrack>
                    <SliderThumb boxSize={4} />
                  </Slider>
                </Box>

                <Box>
                  <Flex justify="space-between" align="center" mb={1}>
                    <Text fontWeight="medium">Voice Volume</Text>
                    <Text fontSize="sm" color="gray.400">
                      {tempSettings.volume.toFixed(2)}
                    </Text>
                  </Flex>
                  <Slider
                    value={tempSettings.volume}
                    onChange={(value) =>
                      setTempSettings((prev) => ({ ...prev, volume: value }))
                    }
                    min={0}
                    max={2.0}
                    step={0.01}
                    focusThumbOnChange={false}
                  >
                    <SliderTrack bg={isDark ? "gray.600" : "gray.200"}>
                      <SliderFilledTrack bg="gray.500" />
                    </SliderTrack>
                    <SliderThumb boxSize={4} />
                  </Slider>
                </Box>

                <ButtonGroup
                  size="sm"
                  w="full"
                  justifyContent="flex-end"
                  spacing={2}
                >
                  <Button
                    variant="ghost"
                    onClick={handleSettingsCancel}
                    colorScheme={isDark ? "black" : "white"}
                    textColor={isDark ? "whiteAlpha" : "gray.800"}
                    _hover={{ bg: isDark ? "gray.600" : "gray.300" }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSettingsSave}
                    colorScheme="gray"
                    _hover={{ bg: isDark ? "gray.600" : "gray.300" }}
                    background={isDark ? "gray.700" : "gray.200"}
                    textColor={isDark ? "whiteAlpha" : "gray.800"}
                  >
                    Save
                  </Button>
                </ButtonGroup>
              </VStack>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </HStack>
    );
  }
);

VoiceSelect.displayName = "VoiceSelect";
